import { useState, useEffect } from "react";
import styles from "./Home.module.css";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { 
  FiArrowRight, FiCheck, FiAward, FiUsers, FiTrendingUp, 
  FiClock, FiShield, FiTarget, FiZap,
  FiCode, FiSmartphone, FiShoppingCart, FiDatabase,
  FiCpu, FiSearch, FiMap, FiPenTool, FiCheckCircle,
  FiStar, FiGlobe, FiHeart, FiMessageCircle, FiCalendar,
  FiBriefcase, FiBarChart2, FiLayers, FiGithub, FiBell, FiInstagram, FiYoutube
} from "react-icons/fi";

import ContactSection from "../Contact/Contact";
import { useNavigate, useLocation } from "react-router-dom";


const Home = () => {
  const colors = {
    primary: "#B08B35",
    secondary: "#AF8A32",
    accent: "#C5AA6B",
    black: "#000000",
    white: "#FFFFFF"
  };

  // Initialize hooks at the top
  const navigate = useNavigate();
  const location = useLocation();

  // Animation controls
  const controls = {
    hero: useAnimation(),
    services: useAnimation(),
    about: useAnimation(),
    process: useAnimation(),
    stats: useAnimation(),
    testimonials: useAnimation(),
    upcoming: useAnimation(),
    industries: useAnimation(),
    portfolio: useAnimation(),
    trust: useAnimation(),
    founder: useAnimation(),
    blog: useAnimation()
  };

  // Simple scroll to section function
  // REPLACE your existing scrollToSection function with this:
const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    // Get the element's position
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - 80;
    
    // Use requestAnimationFrame for smoother scrolling
    requestAnimationFrame(() => {
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    });
  }
};

// Also update handleContactClick:
const handleContactClick = () => {
  const contactSection = document.getElementById('contact');
  if (contactSection) {
    requestAnimationFrame(() => {
      const elementPosition = contactSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 80;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth"
      });
    });
  } else if (location.pathname !== "/") {
    navigate("/");
    setTimeout(() => {
      const contactElement = document.getElementById('contact');
      if (contactElement) {
        requestAnimationFrame(() => {
          const elementPosition = contactElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - 80;
          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth"
          });
        });
      }
    }, 500);
  }
};
  // Handle consultation booking - FIXED with correct Calendly link
  const handleBookConsultation = () => {
   navigate("/contact");

  };

  // Handle WhatsApp chat with your number
  const handleWhatsAppChat = () => {
    window.open('https://wa.me/919154884214?text=Hi%20Brandverse%2C%20I%27m%20interested%20in%20your%20services.%20Can%20we%20discuss%20my%20project%3F', '_blank');
  };

  // Handle form submission - redirect to contact section
  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleContactClick();
  };

 const testimonials = [
  {
    quote: "Working with BRANDVERSE helped us launch our startup website quickly and professionally. The team understood our vision and delivered exactly what we needed.",
    author: "Rohit Kumar",
    company: "Local Business Startup",
    role: "Founder",
    rating: 5,
    project: "Business Website",
    result: "Strong online presence"
  },
  {
    quote: "BRANDVERSE supported us in building our first product MVP. Their guidance and technical support made the development process smooth and efficient.",
    author: "Priya Sharma",
    company: "EduTech Startup",
    role: "Co-Founder",
    rating: 5,
    project: "Product MVP",
    result: "Successful product launch"
  },
  {
    quote: "The team delivered our platform with great attention to detail. As a startup, we needed a reliable technology partner, and BRANDVERSE delivered.",
    author: "Anil Kumar",
    company: "Service Platform",
    role: "Founder",
    rating: 5,
    project: "Web Platform",
    result: "Improved user engagement"
  },
  {
    quote: "BRANDVERSE helped us design and develop our digital platform from scratch. Their ideas and execution played a key role in our early growth.",
    author: "Neha Gupta",
    company: "Tech Startup",
    role: "Product Lead",
    rating: 5,
    project: "Startup Platform",
    result: "Positive user feedback"
  },
  {
    quote: "The ERP system developed by BRANDVERSE helped us streamline our internal operations. Managing records and daily processes became much easier and more organized.",
    author: "Karthik Varma",
    company: "Manufacturing Startup",
    role: "Operations Manager",
    rating: 5,
    project: "ERP System",
    result: "Improved workflow management"
  }
];

  // Portfolio/Case Studies Data
  const portfolioItems = [
    {
      project: "FinTech Startup Website",
      client: "PaySmart India",
      result: "Increased online leads by 200%",
      category: "Web Development",
      image: "/images/portfolio/fintech.jpg",
      metrics: "200% lead growth"
    },
    {
      project: "EduTech Mobile App",
      client: "LearnFast Platform",
      result: "10,000+ users in first quarter",
      category: "Mobile App",
      image: "/images/portfolio/edtech.jpg",
      metrics: "10K+ users"
    },
    {
      project: "ERP System",
      client: "Manufacturing Co.",
      result: "Reduced manual work by 60%",
      category: "Enterprise",
      image: "/images/portfolio/erp.jpg",
      metrics: "60% efficiency"
    },
    {
      project: "AI Chatbot Solution",
      client: "E-commerce Store",
      result: "Customer support automation",
      category: "AI Solutions",
      image: "/images/portfolio/ai.jpg",
      metrics: "24/7 support"
    },
    {
      project: "Healthcare Platform",
      client: "MediCare Network",
      result: "Patient management system",
      category: "Healthcare",
      image: "/images/portfolio/healthcare.jpg",
      metrics: "500+ patients"
    },
    {
      project: "Real Estate Portal",
      client: "PropertyHub",
      result: "Property listing platform",
      category: "Real Estate",
      image: "/images/portfolio/realestate.jpg",
      metrics: "1000+ listings"
    }
  ];

  // Industries Data
  const industries = [
    {
      name: "Startups",
      icon: <FiZap />,
      description: "MVP development, product scaling, tech consulting",
      color: "#B08B35"
    },
    {
      name: "Education & EdTech",
      icon: <FiUsers />,
      description: "Learning platforms, student portals, LMS solutions",
      color: "#AF8A32"
    },
    
    {
      name: "Real Estate",
      icon: <FiMap />,
      description: "Property portals, CRM systems, virtual tours",
      color: "#B08B35"
    },
    {
      name: "Small & Medium Businesses",
      icon: <FiBriefcase />,
      description: "Digital transformation, automation, growth solutions",
      color: "#AF8A32"
    }
  ];

  // Blog Posts Data
  const blogPosts = [
    {
      title: "Top 10 AI Tools for Businesses in 2025",
      excerpt: "Discover how AI can transform your business operations and drive growth.",
      date: "March 15, 2025",
      category: "AI Solutions",
      readTime: "5 min read",
      slug: "top-10-ai-tools"
    },
    {
      title: "How Startups Build MVP Products Successfully",
      excerpt: "A step-by-step guide to building and launching your minimum viable product.",
      date: "March 10, 2025",
      category: "Startups",
      readTime: "7 min read",
      slug: "startup-mvp-guide"
    },
    {
      title: "Why Every Business Needs Automation in 2025",
      excerpt: "Learn how automation can save time, reduce costs, and improve efficiency.",
      date: "March 5, 2025",
      category: "Automation",
      readTime: "6 min read",
      slug: "business-automation"
    }
  ];

  // Founder/Leadership Data
 
// Founder data with 3 founders

  // Trust Indicators Data
  const trustIndicators = [
    { icon: <FiAward />, label: "MSME ", color: "#B08B35" },
    { icon: <FiCheckCircle />, label: "ISO Certified", color: "#AF8A32" },
    { icon: <FiStar />, label: "DPIIT Recognized", color: "#C5AA6B" },
    { icon: <FiUsers />, label: "Hackathons Org.", color: "#B08B35" },
    { icon: <FiGlobe />, label: "Ecosystem ", color: "#AF8A32" }
  ];

  // Service Categories with Outcomes
  const serviceCategories = [
  {
    category: "Web Development",
    icon: <FiCode />,
    outcomes: [
      "Business websites that convert",
      "Startup MVP development",
      "Scalable web applications",
      "E-commerce platforms",
      "SEO-optimized websites",
      "Website maintenance & performance optimization"
    ],
    gradient: "linear-gradient(135deg, #B08B35 0%, #AF8A32 100%)"
  },
  {
    category: "Mobile App Development",
    icon: <FiSmartphone />,
    outcomes: [
      "Android & iOS apps",
      "Cross-platform solutions",
      "Startup product development",
      "App store optimization",
      "UI/UX focused mobile experiences",
      "App performance monitoring & updates"
    ],
    gradient: "linear-gradient(135deg, #B08B35 0%, #C5AA6B 100%)"
  },
  {
    category: "Enterprise Solutions",
    icon: <FiDatabase />,
    outcomes: [
      "ERP systems",
      "HRMS platforms",
      "Business automation",
      "Custom workflows",
      "CRM system development",
      "Enterprise data management solutions"
    ],
    gradient: "linear-gradient(135deg, #AF8A32 0%, #B08B35 100%)"
  },
  {
    category: "AI Solutions",
    icon: <FiCpu />,
    outcomes: [
      "AI chatbots",
      "Automation tools",
      "AI analytics",
      "Intelligent systems",
      "Predictive data insights",
      "AI-powered business process optimization"
    ],
    gradient: "linear-gradient(135deg, #C5AA6B 0%, #B08B35 100%)"
  }
];

  // Intersection observers
  const [heroRef, heroInView] = useInView({ threshold: 0.3, triggerOnce: true });
  const [servicesRef, servicesInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [aboutRef, aboutInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [processRef, processInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [statsRef, statsInView] = useInView({ threshold: 0.3, triggerOnce: true });
  const [testimonialsInViewRef, testimonialsInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [upcomingRef, upcomingInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [industriesRef, industriesInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [portfolioRef, portfolioInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [trustRef, trustInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [founderRef, founderInView] = useInView({ threshold: 0.2, triggerOnce: true });
  const [blogRef, blogInView] = useInView({ threshold: 0.2, triggerOnce: true });

  useEffect(() => {
    const animateSections = async () => {
      const animationPromises = [];
      
      if (heroInView) animationPromises.push(controls.hero.start("visible"));
      if (servicesInView) animationPromises.push(controls.services.start("visible"));
      if (aboutInView) animationPromises.push(controls.about.start("visible"));
      if (processInView) animationPromises.push(controls.process.start("visible"));
      if (statsInView) animationPromises.push(controls.stats.start("visible"));
      if (testimonialsInView) animationPromises.push(controls.testimonials.start("visible"));
      if (upcomingInView) animationPromises.push(controls.upcoming.start("visible"));
      if (industriesInView) animationPromises.push(controls.industries.start("visible"));
      if (portfolioInView) animationPromises.push(controls.portfolio.start("visible"));
      if (trustInView) animationPromises.push(controls.trust.start("visible"));
      if (founderInView) animationPromises.push(controls.founder.start("visible"));
      if (blogInView) animationPromises.push(controls.blog.start("visible"));
      
      await Promise.all(animationPromises);
    };

    animateSections();
  }, [
    heroInView, servicesInView, aboutInView, processInView, 
    statsInView, testimonialsInView, upcomingInView, industriesInView,
    portfolioInView, trustInView, founderInView, blogInView,
    controls.hero, controls.services, controls.about, controls.process,
    controls.stats, controls.testimonials, controls.upcoming, controls.industries,
    controls.portfolio, controls.trust, controls.founder, controls.blog
  ]);

  // Handle scroll from navbar - FIXED AND IMPROVED
  useEffect(() => {
    const handleScrollToSection = () => {
      const state = location.state;
      if (state && state.scrollTo) {
        console.log("Scroll to section requested:", state.scrollTo);
        
        // Increased timeout to ensure DOM is fully loaded
        setTimeout(() => {
          const element = document.getElementById(state.scrollTo);
          if (element) {
            console.log("Found element, scrolling to:", state.scrollTo);
            const offset = 80;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;
            
            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth"
            });
          } else {
            console.log("Element not found:", state.scrollTo);
          }
        }, 500);
      }
    };

    handleScrollToSection();
  }, [location]);
// Add this useEffect to ensure smooth scrolling with the navbar offset
useEffect(() => {
  // This function handles scrolling to sections
  const handleScrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      // Use scrollIntoView with the built-in scroll margin from CSS
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  // Listen for scroll events from navbar
  const handleScrollEvent = (event) => {
    const sectionId = event.detail.section;
    handleScrollToSection(sectionId);
  };

  // Also handle navigation state
  if (location.state?.scrollTo) {
    setTimeout(() => {
      handleScrollToSection(location.state.scrollTo);
    }, 500);
  }

  window.addEventListener('scrollToSection', handleScrollEvent);
  return () => window.removeEventListener('scrollToSection', handleScrollEvent);
}, [location.state]);
  // ADD THIS NEW EFFECT - Listen for direct scroll events from navbar
  // In Home.jsx, add this useEffect (already included in the updated Home.jsx I provided)
useEffect(() => {
  const handleScrollEvent = (event) => {
    const sectionId = event.detail.section;
    console.log("Home: Scroll event received for section:", sectionId);
    
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        const offset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;
        
        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth"
        });
      } else {
        console.log("Home: Section not found:", sectionId);
      }
    }, 100);
  };

  window.addEventListener('scrollToSection', handleScrollEvent);
  return () => window.removeEventListener('scrollToSection', handleScrollEvent);
}, []);

  // ADD THIS NEW EFFECT - Debug which sections are available
  useEffect(() => {
    console.log("Checking available sections on home page:");
    const sections = ['home', 'services', 'about', 'testimonials', 'blog', 'industries', 'trust', 'leadership', 'contact'];
    sections.forEach(id => {
      const element = document.getElementById(id);
      console.log(`Section "${id}": ${element ? '✅ FOUND' : '❌ MISSING'}`);
    });
  }, []);

  const sectionVariants = {
    hidden: { opacity: 0, y: 60 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const staggerItem = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 40, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.6, ease: "easeOut" }
    },
    hover: {
      y: -8,
      scale: 1.02,
      transition: { duration: 0.3, ease: "easeOut" }
    }
  };

  // Process Steps
  const processSteps = [
    {
      title: "Discovery & Analysis",
      description: "We analyze your business goals, target audience and technical requirements",
      icon: <FiSearch />,
      number: "01"
    },
    {
      title: "Strategy & Planning",
      description: "Creating a customized roadmap and strategy for your project success",
      icon: <FiMap />,
      number: "02"
    },
    {
      title: "Design & Development",
      description: "Implementing solutions with precision, care and cutting-edge technologies",
      icon: <FiPenTool />,
      number: "03"
    },
    {
      title: "Testing & Launch",
      description: "Thorough testing and seamless deployment with ongoing support",
      icon: <FiCheckCircle />,
      number: "04"
    }
  ];

  // Stats Data
  const stats = [
    {
      value: "300%",
      label: "Boost in Brand Engagement",
      icon: <FiTrendingUp />,
      delay: 0
    },
    {
      value: "40%",
      label: "Faster Go-to-Market Execution",
      icon: <FiClock />,
      delay: 0.1
    },
    {
      value: "2x",
      label: "Improvement in Project Efficiency",
      icon: <FiZap />,
      delay: 0.2
    },
    {
      value: "100%",
      label: "Client Trust & Retention Rate",
      icon: <FiHeart />,
      delay: 0.3
    }
  ];

  return (

    
    <motion.div className={styles.home}>
      
      {/* Professional Hero Section */}
      <section id="home" className={styles.hero} ref={heroRef}>
        <div className={styles.heroBackground}>
          <div className={styles.heroGradient}></div>
          <div className={styles.heroPattern}></div>
        </div>
        <div className={styles.container}>
          <motion.div 
            className={styles.heroContent}
            variants={sectionVariants}
            initial="hidden"
            animate={controls.hero}
          >
            <div className={styles.heroGrid}>
              {/* Left Content */}
              <motion.div 
                className={styles.heroText}
                variants={staggerContainer}
              >
                <motion.span 
                  className={styles.heroBadge}
                  variants={staggerItem}
                  whileHover={{ scale: 1.05 }}
                >
                  Powered by Innovation
                </motion.span>
                
                <motion.h1 className={styles.heroTitle} variants={staggerItem}>
                  Building Scalable Digital Products & <span style={{ color: colors.primary }}>AI Solutions</span> for Modern Businesses
                </motion.h1>
                
                <motion.p className={styles.heroSubtitle} variants={staggerItem}>
                  We help startups and businesses build websites, applications, and intelligent 
                  automation systems that accelerate growth and drive measurable results.
                </motion.p>
                
                <motion.div className={styles.heroButtons} variants={staggerItem}>
                  <motion.button
                    onClick={handleBookConsultation}
                    className={styles.btnPrimary}
                    initial={{ backgroundColor: '#D4AF37', color: '#FFFFFF', borderColor: 'transparent' }}
                    whileHover={{ 
                      scale: 1.05,
                      y: -2,
                      backgroundColor: '#8B6B1F',
                      color: '#FFFFFF',
                      borderColor: '#D4AF37',
                      boxShadow: '0 10px 25px rgba(212, 175, 55, 0.4)',
                      transition: { duration: 0.3 }
                    }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span>Get Free Consultation</span>
                    <FiArrowRight />
                  </motion.button>
                </motion.div>
                
                {/* Trust Indicators */}
                <motion.div className={styles.trustIndicators} variants={staggerItem}>
                  <motion.div 
                    className={styles.trustItem} 
                    whileHover={{ scale: 1.05, y: -2 }}
                  >
                    <span className={styles.trustNumber}>50+</span>
                    <span className={styles.trustText}>Projects Delivered</span>
                  </motion.div>
                  <motion.div 
                    className={styles.trustItem} 
                    whileHover={{ scale: 1.05, y: -2 }}
                  >
                    <span className={styles.trustNumber}>97%</span>
                    <span className={styles.trustText}>Client Satisfaction</span>
                  </motion.div>
                  <motion.div 
                    className={styles.trustItem} 
                    whileHover={{ scale: 1.05, y: -2 }}
                  >
                    <span className={styles.trustNumber}>24/7</span>
                    <span className={styles.trustText}>Support Available</span>
                  </motion.div>
                </motion.div>
              </motion.div>

              {/* Right Side Wrapper with Image */}
              <motion.div 
                className={styles.heroImageWrapper}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <div className={styles.imageWrapper}>
                  <div className={styles.imageBackground}></div>
                  <motion.div 
                    className={styles.imageContainer}
                    animate={{ 
                      scale: [1, 1.02, 1],
                      rotate: [0, 1, 0]
                    }}
                    transition={{ 
                      duration: 8, 
                      repeat: Infinity,
                      repeatType: "reverse"
                    }}
                  >
                    <img 
                      src="/images/corporate.jpg" 
                      alt="Digital Solutions Showcase" 
                      className={styles.heroImage}
                    />
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Professional Services Section */}
      <section id="services" className={styles.services} ref={servicesRef}>
        <div className={styles.container}>
          <motion.div className={styles.sectionHeader} variants={sectionVariants} initial="hidden" animate={controls.services}>
            <motion.span className={styles.sectionSubtitle} whileHover={{ scale: 1.05 }}>What We Do</motion.span>
            <h2 className={styles.sectionTitle}>Comprehensive Digital Solutions</h2>
            <p className={styles.sectionDescription}>We provide end-to-end services tailored to your business needs, with clear outcomes you can measure.</p>
          </motion.div>

          <motion.div className={styles.servicesGrid} variants={staggerContainer} initial="hidden" animate={controls.services}>
            {serviceCategories.map((category, index) => (
              <motion.div 
                key={index} 
                className={styles.serviceCard} 
                variants={cardVariants} 
                whileHover="hover" 
                custom={index}
              >
                <motion.div 
                  className={styles.serviceIconWrapper} 
                  style={{ background: category.gradient }} 
                  whileHover={{ rotate: 5, scale: 1.05 }}
                >
                  {category.icon}
                </motion.div>
                <h3 className={styles.serviceTitle}>{category.category}</h3>
                <ul className={styles.serviceFeatures}>
                  {category.outcomes.map((outcome, idx) => (
                    <motion.li 
                      key={idx} 
                      initial={{ opacity: 0, x: -20 }} 
                      whileInView={{ opacity: 1, x: 0 }} 
                      transition={{ delay: index * 0.1 + idx * 0.05 }}
                    >
                      <FiCheck className={styles.featureCheck} />
                      {outcome}
                    </motion.li>
                  ))}
                </ul>
                <motion.button 
                  className={styles.serviceButton} 
                  whileHover={{ scale: 1.05, x: 5 }} 
                  whileTap={{ scale: 0.95 }} 
                  onClick={handleBookConsultation}
                >
                  Discuss Your Project <FiArrowRight />
                </motion.button>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Industries We Serve Section */}
      <section id="industries" className={styles.industries} ref={industriesRef}>
        <div className={styles.container}>
          <motion.div className={styles.sectionHeader} variants={sectionVariants} initial="hidden" animate={controls.industries}>
            <motion.span className={styles.sectionSubtitle} whileHover={{ scale: 1.05 }}>Who We Help</motion.span>
            <h2 className={styles.sectionTitle}>Industries We Serve</h2>
            <p className={styles.sectionDescription}>We bring deep domain expertise to deliver solutions that truly understand your industry.</p>
          </motion.div>

          <motion.div className={styles.industriesGrid} variants={staggerContainer} initial="hidden" animate={controls.industries}>
            {industries.map((industry, index) => (
              <motion.div 
                key={index} 
                className={styles.industryCard} 
                variants={cardVariants}
                whileHover="hover"
              >
                <motion.div 
                  className={styles.industryIcon}
                  style={{ backgroundColor: `${industry.color}15`, color: industry.color }}
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  {industry.icon}
                </motion.div>
                <h3 className={styles.industryName}>{industry.name}</h3>
                <p className={styles.industryDescription}>{industry.description}</p>
                <motion.button 
                  className={styles.industryButton}
                  whileHover={{ x: 5 }}
                  onClick={handleBookConsultation}
                >
                  Learn More <FiArrowRight />
                </motion.button>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Trust & Credibility Section */}
      <section id="trust" className={styles.trust} ref={trustRef}>
        <div className={styles.container}>
          <motion.div 
            className={styles.sectionHeader} 
            variants={sectionVariants} 
            initial="hidden" 
            animate={controls.trust}
          >
            <motion.span 
              className={styles.sectionSubtitle} 
              whileHover={{ scale: 1.05 }}
            >
              Why Trust Us
            </motion.span>
            <h2 className={styles.sectionTitle}>Trusted & Verified Partner</h2>
            <p className={styles.sectionDescription}>We're proud to be recognized by leading authorities for our quality and commitment.</p>
          </motion.div>

          <motion.div 
            className={styles.trustGrid} 
            variants={staggerContainer} 
            initial="hidden" 
            animate={controls.trust}
          >
            {trustIndicators.map((item, index) => (
              <motion.div 
                key={index} 
                className={styles.trustCard}
                variants={cardVariants}
                whileHover="hover"
                whileTap={{ scale: 0.98 }}
              >
                <motion.div 
                  className={styles.trustIcon}
                  style={{ color: item.color }}
                  whileHover={{ scale: 1.2, rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  {item.icon}
                </motion.div>
                <span className={styles.trustLabel}>{item.label}</span>
              </motion.div>
            ))}
          </motion.div>

          <motion.div 
            className={styles.recognitionBadges}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            viewport={{ once: true }}
          >
            <span className={styles.badge}>MSME Registered</span>
            <span className={styles.badge}>ISO 9001:2024</span>
            <span className={styles.badge}>Startup India DPIIT</span>
            <span className={styles.badge}>Hackathon Partner</span>
            <span className={styles.badge}>Ecosystem Member</span>
          </motion.div>
        </div>
      </section>

      {/* About Section - Without Certificate Images, More Interactive */}
      <section id="about" className={styles.about} ref={aboutRef}>
        <div className={styles.container}>
          <motion.div
            className={styles.aboutContent}
            variants={sectionVariants}
            initial="hidden"
            animate={controls.about}
          >
            {/* Left: Text Section */}
            <div className={styles.aboutText}>
              <span className={styles.sectionSubtitle}>Our Story</span>
              <h2 className={styles.sectionTitle}>
                Empowering Digital Transformation Since 2025
              </h2>
              <p className={styles.aboutDescription}>
                Founded in 2025, <strong>Brandverse Technologies India Pvt. Ltd.</strong> is an
                innovation-driven technology company focused on building impactful digital
                solutions for startups, businesses, and institutions. We specialize in
                web development, enterprise software, mobile applications, AI-powered systems,
                and scalable digital platforms that help organizations transform ideas into
                powerful technology products.
              </p>
              <p className={styles.aboutDescription}>
                At Brandverse, we believe technology should solve real-world challenges.
                Beyond providing digital services, we are committed to creating innovative
                products that address societal needs — especially empowering students and
                future innovators. Our vision is to build intelligent ecosystems that connect
                technology, education, and entrepreneurship to drive meaningful progress.
              </p>

              {/* Stats Section */}
              <div className={styles.aboutStats}>
                <motion.div
                  className={styles.statItem}
                  whileHover={{ scale: 1.05, y: -5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <FiAward className={styles.statIcon} />
                  <div className={styles.statContent}>
                    <h3>50+</h3>
                    <p>Successful Projects</p>
                  </div>
                </motion.div>

                <motion.div
                  className={styles.statItem}
                  whileHover={{ scale: 1.05, y: -5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <FiUsers className={styles.statIcon} />
                  <div className={styles.statContent}>
                    <h3>97%</h3>
                    <p>Happy Clients</p>
                  </div>
                </motion.div>

                <motion.div
                  className={styles.statItem}
                  whileHover={{ scale: 1.05, y: -5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <FiTrendingUp className={styles.statIcon} />
                  <div className={styles.statContent}>
                    <h3>100%</h3>
                    <p>Success Rate</p>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Right: Interactive Visual Section */}
            <div className={styles.aboutVisual}>
              <motion.div
                className={styles.visualContent}
                whileHover={{ y: -5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {/* Core Values */}
                <div className={styles.valuesSection}>
                  <h4>What Makes Us Different</h4>
                  <div className={styles.valuesGrid}>
                    <motion.div 
                      className={styles.valueCard}
                      whileHover={{ scale: 1.05, y: -3 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <div className={styles.valueIcon}><FiZap /></div>
                      <h5>Innovation First</h5>
                      <p>We stay ahead of tech trends to deliver cutting-edge solutions</p>
                    </motion.div>

                    <motion.div 
                      className={styles.valueCard}
                      whileHover={{ scale: 1.05, y: -3 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <div className={styles.valueIcon}><FiTarget /></div>
                      <h5>Results Driven</h5>
                      <p>Every project has clear, measurable goals from day one</p>
                    </motion.div>

                    <motion.div 
                      className={styles.valueCard}
                      whileHover={{ scale: 1.05, y: -3 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <div className={styles.valueIcon}><FiShield /></div>
                      <h5>Quality Assured</h5>
                      <p>Rigorous testing ensures bug-free, reliable products</p>
                    </motion.div>

                    <motion.div 
                      className={styles.valueCard}
                      whileHover={{ scale: 1.05, y: -3 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <div className={styles.valueIcon}><FiClock /></div>
                      <h5>On-Time Delivery</h5>
                      <p>We respect your deadlines and deliver as promised</p>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      
      
      {/* Professional Process Section */}
      <section className={styles.process} ref={processRef}>
        <div className={styles.container}>
          <motion.div className={styles.sectionHeader} variants={sectionVariants} initial="hidden" animate={controls.process}>
            <span className={styles.sectionSubtitle}>How We Work</span>
            <h2 className={styles.sectionTitle}>The Brandverse Process</h2>
            <p className={styles.sectionDescription}>A structured approach that ensures quality, efficiency, and measurable success.</p>
          </motion.div>

          <motion.div className={styles.processSteps} variants={staggerContainer} initial="hidden" animate={controls.process}>
            {processSteps.map((step, index) => (
              <motion.div key={index} className={styles.processStep} variants={staggerItem} whileHover={{ scale: 1.05 }}>
                <motion.div className={styles.processNumber} whileHover={{ rotate: 360 }} transition={{ duration: 0.6 }}>{step.number}</motion.div>
                <div className={styles.processIcon}>{step.icon}</div>
                <div className={styles.processContent}>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <motion.div className={styles.processConnector} initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ delay: index * 0.2 + 0.5 }} />
                )}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Startup Tech Partner Program Section */}
      <section id="tech-partner" className={styles.techPartnerWhite}>
        <div className={styles.container}>
          <motion.div 
            className={styles.techPartnerContent} 
            variants={sectionVariants} 
            initial="hidden" 
            whileInView="visible" 
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.div 
              className={styles.techPartnerBadgeLight}
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5, type: "spring" }}
              whileHover={{ scale: 1.1, boxShadow: '0 10px 25px rgba(176, 139, 53, 0.3)' }}
            >
              <motion.div animate={{ rotate: [0, 15, -15, 0], scale: [1, 1.2, 1] }} transition={{ duration: 2, repeat: Infinity }}>
                <FiZap className={styles.techPartnerIconLight} />
              </motion.div>
              <span>Advanced Growth Feature</span>
            </motion.div>

            <motion.h2 className={styles.sectionTitle} style={{ color: '#1A1A1A' }} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }}>
              Startup Tech Partner Program
            </motion.h2>

            <motion.p className={styles.sectionDescription} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.6 }}>
              Not just a vendor - become our technology partner for long-term success
            </motion.p>
            
            <motion.div 
              className={styles.techPartnerGrid}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: { staggerChildren: 0.2, delayChildren: 0.5 }
                }
              }}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <motion.div className={styles.techPartnerCardLight} variants={{ hidden: { opacity: 0, y: 50 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, type: "spring" } } }} whileHover={{ y: -15, boxShadow: '0 30px 40px rgba(176, 139, 53, 0.15)', borderColor: '#B08B35' }} whileTap={{ scale: 0.98 }}>
                <motion.div className={styles.cardIconLight} whileHover={{ rotate: 360, scale: 1.2, transition: { duration: 0.6 } }}><FiCode /></motion.div>
                <h3>MVP Development</h3>
                <p>Take your idea from concept to market-ready product with our agile MVP development process</p>
                <motion.div className={styles.cardUnderline} initial={{ scaleX: 0 }} whileHover={{ scaleX: 1 }} transition={{ duration: 0.3 }} />
              </motion.div>
              
              <motion.div className={styles.techPartnerCardLight} variants={{ hidden: { opacity: 0, y: 50 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, type: "spring" } } }} whileHover={{ y: -15, boxShadow: '0 30px 40px rgba(176, 139, 53, 0.15)', borderColor: '#B08B35' }} whileTap={{ scale: 0.98 }}>
                <motion.div className={styles.cardIconLight} whileHover={{ rotate: 360, scale: 1.2, transition: { duration: 0.6 } }}><FiLayers /></motion.div>
                <h3>Product Scaling</h3>
                <p>Scale your product architecture to handle millions of users without compromising performance</p>
                <motion.div className={styles.cardUnderline} initial={{ scaleX: 0 }} whileHover={{ scaleX: 1 }} transition={{ duration: 0.3 }} />
              </motion.div>
              
              <motion.div className={styles.techPartnerCardLight} variants={{ hidden: { opacity: 0, y: 50 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, type: "spring" } } }} whileHover={{ y: -15, boxShadow: '0 30px 40px rgba(176, 139, 53, 0.15)', borderColor: '#B08B35' }} whileTap={{ scale: 0.98 }}>
                <motion.div className={styles.cardIconLight} whileHover={{ rotate: 360, scale: 1.2, transition: { duration: 0.6 } }}><FiBarChart2 /></motion.div>
                <h3>Tech Consulting</h3>
                <p>Strategic technology guidance to make the right architectural decisions for your startup</p>
                <motion.div className={styles.cardUnderline} initial={{ scaleX: 0 }} whileHover={{ scaleX: 1 }} transition={{ duration: 0.3 }} />
              </motion.div>
            </motion.div>
            
            <motion.div initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} transition={{ delay: 1.2, duration: 0.5, type: "spring" }} viewport={{ once: true }}>
              <motion.button className={styles.btnPrimary} whileHover={{ scale: 1.08, y: -4, boxShadow: '0 25px 35px rgba(176, 139, 53, 0.4)' }} whileTap={{ scale: 0.95 }} onClick={handleContactClick}>
                <span>Become a Partner</span>
                <FiArrowRight />
                <motion.span className={styles.buttonRipple} initial={{ scale: 0, opacity: 0.5 }} whileHover={{ scale: 2, opacity: 0 }} transition={{ duration: 0.6 }} />
              </motion.button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Professional Testimonials Section */}
      <section id="testimonials" className={styles.testimonials} ref={testimonialsInViewRef}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionSubtitle}>Client Success Stories</span>
            <h2 className={styles.sectionTitle}>What Our Clients Say</h2>
            <p className={styles.sectionDescription}>Don't just take our word for it – hear from businesses we've helped transform</p>
          </div>

          <div className={styles.sliderWrapper}>
            <div className={styles.sliderTrack}>
              {[...testimonials, ...testimonials, ...testimonials].map((t, index) => (
                <div key={index} className={styles.testimonialCard}>
                  <div className={styles.rating}>
                    {[...Array(t.rating)].map((_, i) => (
                      <FiStar key={i} className={styles.starIcon} />
                    ))}
                  </div>
                  <p className={styles.testimonialQuote}>"{t.quote}"</p>
                  <div className={styles.testimonialMeta}>
                    <span className={styles.testimonialProject}>{t.project}</span>
                    <span className={styles.testimonialResult}>{t.result}</span>
                  </div>
                  <div className={styles.testimonialAuthor}>
                    <div className={styles.authorAvatar}>{t.author[0]}</div>
                    <div>
                      <h4 className={styles.authorName}>{t.author}</h4>
                      <p className={styles.authorRole}>{t.role}</p>
                      <p className={styles.authorCompany}>{t.company}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


     {/* Blog / Insights Section */}
<section id="blog" className={styles.blog} ref={blogRef}>
  <div className={styles.container}>
    <motion.div 
      className={styles.sectionHeader} 
      variants={sectionVariants} 
      initial="hidden" 
      animate={controls.blog}
    >
      <motion.span 
        className={styles.sectionSubtitle} 
        whileHover={{ scale: 1.05 }}
      >
        Insights & Resources
      </motion.span>
      <h2 className={styles.sectionTitle}>Latest from Our Blog</h2>
      <p className={styles.sectionDescription}>Expert insights, trends, and guides for modern businesses</p>
    </motion.div>

    <motion.div 
      className={styles.blogGrid} 
      variants={staggerContainer} 
      initial="hidden" 
      animate={controls.blog}
    >
      {blogPosts.map((post, index) => (
        <motion.article 
          key={index} 
          className={styles.blogCard}
          variants={cardVariants}
          whileHover="hover"
        >
          <div className={styles.blogCategory}>{post.category}</div>
          <h3 className={styles.blogTitle}>{post.title}</h3>
          <p className={styles.blogExcerpt}>{post.excerpt}</p>
          <div className={styles.blogMeta}>
            <span className={styles.blogDate}>{post.date}</span>
            <span className={styles.blogReadTime}>{post.readTime}</span>
          </div>
          <motion.a 
            onClick={() => navigate('/blog')}
            className={styles.blogLink}
            whileHover={{ 
              x: 5,
              color: '#B08B35',
              transition: { duration: 0.2 }
            }}
            whileTap={{ scale: 0.95 }}
          >
            Read Article <FiArrowRight />
          </motion.a>
        </motion.article>
      ))}
    </motion.div>

    <motion.div className={styles.blogCTA}>
      <motion.button 
        className={styles.btnSecondary}
        initial={{ backgroundColor: '#ffffff', color: '#1A1A1A', borderColor: 'rgba(212, 175, 55, 0.3)' }}
        whileHover={{ 
          scale: 1.05,
          backgroundColor: '#B08B35',
          color: '#ffffff',
          borderColor: '#B08B35',
          boxShadow: '0 10px 25px rgba(176, 139, 53, 0.4)',
          transition: { duration: 0.3 }
        }}
        whileTap={{ scale: 0.95 }}
        onClick={() => navigate('/blog')}
      >
        <motion.span
          animate={{ x: 0 }}
          whileHover={{ x: 5 }}
          transition={{ duration: 0.2 }}
        >
          View All Articles
        </motion.span>
        <motion.span
          animate={{ x: 0 }}
          whileHover={{ x: 5 }}
          transition={{ duration: 0.2 }}
        >
          <FiArrowRight />
        </motion.span>
      </motion.button>
    </motion.div>
  </div> 
</section>
{/* Brandverse StudioX - New Unit Announcement */}
{/* Brandverse StudioX - New Unit Announcement */}
<section id="studiox" className={styles.studiox}>
  <div className={styles.container}>
    <motion.div 
      className={styles.studioxContent}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
    >
      {/* Introduction Text */}
      <motion.div 
        className={styles.studioxIntro}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <span className={styles.introText}>Brandverse Technologies Introducing</span>
        <div className={styles.divider}></div>
      </motion.div>

      {/* Logo */}
      <motion.div 
        className={styles.studioxLogo}
        whileHover={{ scale: 1.05 }}
        transition={{ type: "spring", stiffness: 300 }}
      >
        <img 
          src="/images/studiox-logo.png" 
          alt="Brandverse StudioX" 
          className={styles.logoImage}
        />
      </motion.div>

      {/* Title */}
      <motion.h2 
        className={styles.studioxTitle}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        Brandverse <span className={styles.studioxHighlight}>StudioX</span>
      </motion.h2>

      {/* Description */}
      <motion.p 
        className={styles.studioxDescription}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
       <p>
          Brandverse StudioX is a sub-unit of Brandverse Technologies, created with the vision of transforming 
          how people consume everyday information.
        </p>
        
        <p>
          In a world filled with overwhelming content, our focus is to deliver only what truly matters — 
          including important news, career opportunities, and technology updates — in a clear, concise, 
          and easy-to-understand format.
        </p>
        
        <p>
          Our approach is centered around short, well-structured video content, designed to save time while 
          providing real value. By simplifying complex information, we aim to help individuals stay informed, 
          make better decisions, and stay ahead in a rapidly changing world.
        </p>
      </motion.p>

      {/* What We Cover */}
      <motion.div 
        className={styles.studioxCategories}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        <h3>What You'll Get</h3>
        <div className={styles.categoriesGrid}>
          <div className={styles.categoryItem}>
            <span>🎓</span> Career Opportunities
          </div>
          <div className={styles.categoryItem}>
            <span>📚</span> Education Updates
          </div>
          <div className={styles.categoryItem}>
            <span>💡</span> Tech & Innovation
          </div>
          <div className={styles.categoryItem}>
            <span>📊</span> Business Insights
          </div>
          <div className={styles.categoryItem}>
            <span>🚀</span> Startup & Industry Updates
          </div>
          <div className={styles.categoryItem}>
            <span>⭐</span> Policy & Governance Updates
          </div>
          
        </div>
      </motion.div>

      {/* Social Media Buttons */}
      <motion.div 
        className={styles.studioxSocial}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <h3>Follow & Subscribe</h3>
        <div className={styles.socialButtons}>
          <motion.button 
            className={styles.instagramBtn}
            whileHover={{ scale: 1.05, y: -3 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => window.open('https://www.instagram.com/brandverse_studiox', '_blank')}
          >
            <FiInstagram /> Follow on Instagram
          </motion.button>
          
          <motion.button 
            className={styles.youtubeBtn}
            whileHover={{ scale: 1.05, y: -3 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => window.open('https://www.youtube.com/channel/UC_KA8k6CqfJxaC6McDO6OmA', '_blank')}
          >
            <FiYoutube /> Subscribe on YouTube
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  </div>
</section>
      
      {/* Contact Section */}
      <section id="contact" className={styles.contactSection}>
        <ContactSection />
      </section>
    </motion.div>
  );
};

export default Home;